import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Card, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  renderAllSwitchesCell,
  renderScheduledDateCol,
} from "components/ReactTableColumnHelper";
import Colors from "lookup/Colors.json";
import {
  sendAuthenticatedAsyncRequest,
  sendAuthenticatedAsyncRequestWithFinallyCallback,
} from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";

const ASSIGNED_IN_PROGRESS_STATUSES = ["NEW", "OPEN", "ON_HOLD"];

const ASSIGNED_PAGE_SIZE = 50;

const headerBarStyle = {
  backgroundColor: Colors["rm-blue"],
  color: Colors["rm-white"],
  borderTopLeftRadius: "0.375rem",
  borderTopRightRadius: "0.375rem",
  padding: "0.75rem 1rem",
  fontWeight: 600,
};

/**
 * Call groups currently assigned to the logged-in agent (exact assignee match).
 * Uses a dedicated assigned endpoint — not the list API's assignee-or-unassigned semantics.
 * Independent of the Go filter selections on the parent screen.
 */
const AssignedActivitiesTable = ({ enabled }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  const loadAssigned = useCallback(() => {
    if (!enabled) {
      return;
    }
    setLoading(true);
    setLoadError(false);
    sendAuthenticatedAsyncRequestWithFinallyCallback(
      `/agent-workspace/call-group/assigned?pageSize=${ASSIGNED_PAGE_SIZE}`,
      "GET",
      null,
      (response) => {
        setRows(response.data?.records || []);
      },
      (error) => {
        setLoadError(true);
        setRows([]);
        showNotification({
          title: "Unable to load assigned activities",
          message: getErrorMessage(error) || "Please try again.",
          position: "tr",
          type: "error",
        });
      },
      () => setLoading(false)
    );
  }, [enabled]);

  useEffect(() => {
    loadAssigned();
  }, [loadAssigned]);

  const checkIfCallGroupClaimed = (callGroupId) =>
    new Promise((resolve) => {
      sendAuthenticatedAsyncRequest(
        `/agent-workspace/${callGroupId}/is-callgroup-claimed`,
        "GET",
        null,
        (response) => resolve(Boolean(response.data)),
        () => resolve(false)
      );
    });

  const handleRowClick = async (row) => {
    if (!row?.callGroupId || openingId) {
      return;
    }
    setOpeningId(row.callGroupId);
    try {
      const isClaimed = await checkIfCallGroupClaimed(row.callGroupId);
      if (isClaimed) {
        showNotification({
          title: "Call group unavailable",
          message: "This call group is already claimed by another agent.",
          position: "tr",
          type: "warning",
        });
        return;
      }
      navigate(`/agent-workspace/call-group-details/${row.callGroupId}`, {
        state: {
          status: ASSIGNED_IN_PROGRESS_STATUSES,
          fromAgentAssignedTable: true,
        },
      });
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Card className="shadow-sm border-0" data-testid="assigned-activities-table">
      <div style={headerBarStyle}>Agent Assigned Activities</div>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" size="sm" />
          </div>
        ) : loadError ? (
          <Alert variant="danger" className="m-3 mb-3">
            Could not load assigned activities.
          </Alert>
        ) : rows.length === 0 ? (
          <Alert
            variant="secondary"
            className="m-3 mb-3"
            data-testid="assigned-activities-empty"
          >
            no assigned activities
          </Alert>
        ) : (
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Customer</th>
                <th>Client</th>
                <th>Population Type</th>
                <th>Switches</th>
                <th>Open Switches</th>
                <th>Rank</th>
                <th>Assigned To</th>
                <th>Claimed By</th>
                <th>Scheduled For</th>
                <th>Entity</th>
                <th>Name</th>
                <th>Phone #</th>
                <th>Time Zone</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.callGroupId}
                  role="button"
                  data-testid={`assigned-row-${row.callGroupId}`}
                  style={{ cursor: openingId === row.callGroupId ? "wait" : "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  <td>{row.customerNameAbbreviation}</td>
                  <td>{row.clientNameAbbreviation}</td>
                  <td>{row.populationType}</td>
                  <td>{renderAllSwitchesCell(row.aggregatedSwitchList)}</td>
                  <td>{row.openActivitiesCount}</td>
                  <td>{row.priority}</td>
                  <td>
                    {row.assigneeName ? (
                      <Badge
                        bg="success"
                        pill
                        style={{ backgroundColor: Colors.green }}
                      >
                        {row.assigneeName}
                      </Badge>
                    ) : null}
                  </td>
                  <td>{row.claimerName}</td>
                  <td>
                    {renderScheduledDateCol(row.scheduledDate, row.daysToScheduledDate)}
                  </td>
                  <td>{row.entityType}</td>
                  <td>{row.entityName}</td>
                  <td>{row.phoneNumber}</td>
                  <td>{row.timeZone}</td>
                  <td>{row.type}</td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default AssignedActivitiesTable;
